import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Form, Stack } from 'rsuite';
import { AutocompleteWithErrorStatus } from './AutocompleteWithErrorStatus';
import { lookupAddress } from '../../api/apiAccess';
const DEBOUNCE_DELAY = 500; // 500ms
const MINIMUM_SEARCH_TERM_LENGTH = 3;

const UnrestrictedAddressLookup = ({
  name,
  label,
  validationRule,
  hasError,
  errorMessage,
  hideErrorIcon = false,
  disabled = false,
  onTextChanged = () => {},
  onItemSelected = () => {},
  ...rest
}) => {
  const containerRef = useRef();
  const [menuWidth, setMenuWidth] = useState('0px');
  const [addressOptions, setAddressOptions] = useState([]);
  const [resizeTrigger, setResizeTrigger] = useState(0);
  const [loading, setLoading] = useState(false);
  const debounceTimer = useRef(null);

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleResize = () => {
    setResizeTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    // This is required to prevent a ResizeObserver bug
    // It ensures that the autocomplete suggestions menu is can not attempt to
    // expand beyond the size of the input field
    const newWidth = containerRef.current.clientWidth;
    setMenuWidth(`${newWidth}px`);
  }, [containerRef, resizeTrigger]);

  // we want to display andsearch on the full address
  const parseSmartyResultToAddress = (result) => {
    return {
      label: `${result.street_line} ${result.secondary} ${result.city} ${result.state} ${result.zipcode}`,
      value: `${result.street_line} ${result.secondary} ${result.city} ${result.state} ${result.zipcode}`,
      components: result, // address components
    };
  };

  const debouncedSearch = useCallback(
    async (value) => {
      onTextChanged();
      if (value === undefined || value.length < MINIMUM_SEARCH_TERM_LENGTH) {
        setAddressOptions([]);
        return;
      }
      setLoading(true);
      let { data } = await lookupAddress(value);
      let parsedData = data?.suggestions?.map((suggestion) =>
        parseSmartyResultToAddress(suggestion)
      );
      setAddressOptions(parsedData);
      setLoading(false);
    },
    [onTextChanged]
  );

  const onSearchTermChanged = useCallback(
    (value) => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(() => {
        debouncedSearch(value);
      }, DEBOUNCE_DELAY);
    },
    [debouncedSearch]
  );

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const onAddressSelected = async (address) => {
    onItemSelected(address);
  };

  const AutocompleteWithErrorStateAndProps = useCallback(
    (props) => (
      <AutocompleteWithErrorStatus
        {...props}
        showError={hasError}
        errorMessage={errorMessage}
        hideErrorIcon={hideErrorIcon}
        isLoading={props.loading === 'true'}
      />
    ),
    [hasError, errorMessage, hideErrorIcon]
  );

  return (
    <Form.Group
      controlId={name}
      {...rest.xs}
      data-testid="unrestricted-address-lookup"
    >
      <Stack direction="row">
        <Form.ControlLabel>{label}</Form.ControlLabel>
      </Stack>
      <Stack ref={containerRef}>
        <Form.Control
          className={disabled === true ? 'disabled-input' : null}
          style={{ width: menuWidth }}
          name={name}
          rule={validationRule}
          accepter={AutocompleteWithErrorStateAndProps}
          onChange={onSearchTermChanged}
          onSelect={onAddressSelected}
          data={addressOptions}
          disabled={disabled}
          value={rest.value}
          loading={loading.toString()}
          {...rest}
        />
      </Stack>
    </Form.Group>
  );
};

export default UnrestrictedAddressLookup;
